import React from 'react';
import { Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { useTheme } from '../../context/ThemeContext';
import type { Chip } from '../../utils/walletFormat';

/**
 * Small pieces shared by the Wallet, Pay & top up and Canteen screens (web and
 * native alike).
 */

/** A blocking wait while a request is in flight: the app's loader, no words. */
export function BusyOverlay({ visible }: { visible: boolean }) {
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={() => undefined}>
      <View style={styles.backdrop} accessibilityRole="progressbar">
        <LoadingSpinner size="large" />
      </View>
    </Modal>
  );
}

/** A coloured status chip that follows the theme. */
export function StatusChip({ chip }: { chip: Chip }) {
  const { isDark } = useTheme();
  return (
    <View style={[styles.chip, { backgroundColor: isDark ? chip.darkBg : chip.bg }]}>
      <Text style={[styles.chipText, { color: isDark ? chip.darkFg : chip.fg }]}>{chip.label}</Text>
    </View>
  );
}

/** Two or more options side by side - the screen's own tabs. */
export function Segmented<T extends string>({ options, value, onChange }: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  const { colors } = useTheme();
  return (
    <View style={[styles.segment, { backgroundColor: colors.surface, borderColor: colors.border }]}>
      {options.map(o => {
        const on = o.value === value;
        return (
          <TouchableOpacity
            key={o.value}
            style={[styles.segmentItem, on && { backgroundColor: colors.primary }]}
            onPress={() => onChange(o.value)}
            accessibilityRole="tab"
            accessibilityState={{ selected: on }}
          >
            <Text style={[styles.segmentText, { color: on ? '#FFF' : colors.textSecondary }]}>{o.label}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

/** A short explanation: blue for information, amber for a warning, red for an error. */
export function Notice({ tone = 'info', children, onClose }: {
  tone?: 'info' | 'warn' | 'error';
  children: React.ReactNode;
  onClose?: () => void;
}) {
  const { colors } = useTheme();
  const palette = tone === 'error'
    ? { bg: colors.dangerLight, fg: colors.danger, icon: 'alert-circle' }
    : tone === 'warn'
      ? { bg: colors.warningLight, fg: colors.warning, icon: 'alert-triangle' }
      : { bg: colors.primaryLight, fg: colors.primary, icon: 'info' };
  return (
    <View style={[styles.notice, { backgroundColor: palette.bg }]}>
      <FeatherIcon name={palette.icon} size={16} color={palette.fg} style={{ marginTop: 1 }} />
      <View style={{ flex: 1 }}>
        {typeof children === 'string'
          ? <Text style={[styles.noticeText, { color: colors.textPrimary }]}>{children}</Text>
          : children}
      </View>
      {onClose && (
        <TouchableOpacity onPress={onClose} accessibilityLabel="Dismiss">
          <FeatherIcon name="x" size={16} color={colors.textSecondary} />
        </TouchableOpacity>
      )}
    </View>
  );
}

/** A row of child chips; hidden when there is only one child. */
export function ChildChips({ options, value, onChange }: {
  options: { id: string; label: string }[];
  value: string | null;
  onChange: (id: string) => void;
}) {
  const { colors } = useTheme();
  if (options.length < 2) { return null; }
  return (
    <View style={styles.childRow}>
      {options.map(o => {
        const on = o.id === value;
        return (
          <TouchableOpacity
            key={o.id}
            onPress={() => onChange(o.id)}
            style={[
              styles.childChip,
              { borderColor: on ? colors.primary : colors.border, backgroundColor: on ? colors.primary : colors.surface },
            ]}
            accessibilityState={{ selected: on }}
          >
            <Text style={[styles.childChipText, { color: on ? '#FFF' : colors.textSecondary }]}>{o.label}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.45)', alignItems: 'center', justifyContent: 'center' },
  chip: { borderRadius: 999, paddingHorizontal: 10, paddingVertical: 3, alignSelf: 'flex-start' },
  chipText: { fontSize: 10.5, fontWeight: '800' },
  segment: { flexDirection: 'row', borderRadius: 12, borderWidth: 1, padding: 3, marginBottom: 14 },
  segmentItem: { flex: 1, alignItems: 'center', paddingVertical: 9, borderRadius: 9 },
  segmentText: { fontSize: 13, fontWeight: '700' },
  notice: { flexDirection: 'row', gap: 10, borderRadius: 12, padding: 12, marginBottom: 12 },
  noticeText: { fontSize: 12.5, lineHeight: 18 },
  childRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  childChip: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 999, borderWidth: 1 },
  childChipText: { fontSize: 12.5, fontWeight: '700' },
});
